import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Paper
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useTheme } from "@mui/material/styles";
import AnonymousReporter from "./AnonymousReporter";

const TradeResult = ({
  trade,
  highlightTerm,
  secondHighlightTerm = "",
  isPartialHighlight = false,
  fromTokenAllSearch = false,
  showAnonymousReporter = false,
  anonymousReporter,
}) => {
  const theme = useTheme();
  if (!trade) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(
      `Date: ${trade.tradeDate}\nSent: ${trade.itemsSent}\nReceived: ${trade.itemsReceived}\nNotes: ${trade.notes}`
    );
  };

  const highlightSearchTerm = (text) => {
    if (!highlightTerm) return text;

    const itemParts = text.split(/\s*\+\s*/); // split by +

    return itemParts.map((part, index) => {
      const match = part.match(/^(\d+x\s+)?(.+?)\s*\(\d+(-\d+)?\)$/);

      if (!match) {
        // not a typical item string, return as-is
        return (
          <span key={index}>
            {part}
            {index < itemParts.length - 1 && " + "}
          </span>
        );
      }

      const itemName = match[2].trim().toLowerCase();
      const searchTerm = highlightTerm.trim().toLowerCase();
      const secondSearchTerm = secondHighlightTerm !== "" ? secondHighlightTerm.trim().toLowerCase() : "";
      const includesSecondSearchTerm = itemName.includes(secondSearchTerm)

      let isMatch = false;
      if (fromTokenAllSearch && searchTerm === "essence") {
        if (itemName.includes("essence of") || itemName.includes("treasured ")) {
          isMatch = true;
        }
      }
      else if (fromTokenAllSearch && searchTerm === "prismatic " && itemName.includes("habbo")) {
        // special habbo cases hardcoded for prismatic search
        if (itemName.includes("habbo hotel red") || itemName.includes("habbo hotel yellow") || itemName.includes("habbo hotel green") || itemName.includes("habbo hotel pastel")) {
          isMatch = true;
        }
      }
      else if (isPartialHighlight) {
        // Partial substring match, excluding brush
        if (
          itemName.includes(searchTerm) &&
          (includesSecondSearchTerm || secondSearchTerm === "") &&
          !itemName.includes("styling studio prismatic brush")
        ) {
          isMatch = true;
        }
      }
      else {
        // Exact match for normal search
        isMatch = itemName === searchTerm;
      }

      return (
        <span key={index}>
          {isMatch ? (
            <Box
              component="span"
              sx={{
                color: theme.palette.accent.main,
                fontWeight: 600
              }}
            >
              {part}
            </Box>
          ) : (
            part
          )}
          {index < itemParts.length - 1 && " + "}
        </span>
      );
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, my: 2, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{trade.tradeDate}</Typography>
        <Tooltip title="Copy to clipboard">
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box mt={1}>
        <Typography>
          <strong>Items Sent:</strong> {highlightSearchTerm(trade.itemsSent)}
        </Typography>
        <Typography>
          <strong>Items Received:</strong> {highlightSearchTerm(trade.itemsReceived)}
        </Typography>
        <Typography>
          <strong>Notes:</strong> {trade.notes || "—"}
        </Typography>
      </Box>
      {showAnonymousReporter && anonymousReporter && (
        <AnonymousReporter reporter={anonymousReporter} />
      )}
    </Paper>
  );
};

export default TradeResult;